// Survey theming for the participant-facing survey.
//
// The backend (Welbee-Custom-Api GetSurveyQuestionByParticipantTokenQuery) always
// returns a `surveyTheme` on the participant payload. When no custom theme is saved
// for the survey/school/MAT it returns a hard-coded default with `id === 0`; a real
// saved theme has a DB id > 0. We only override the existing look when a real theme
// is present, so surveys without a custom theme render exactly as before.

// Per-field fallbacks used when a *custom* theme is applied but leaves a field
// blank. These mirror the backend `id: 0` fallback theme, except `buttonColor`,
// which is aligned with the app's existing UI default (`#E84F6B` — see Colors.scss
// and the CSS-var fallbacks in WbButton/WbOutlineButton) so a custom theme that
// omits the button colour keeps the current button look instead of shifting it.
export const DEFAULT_SURVEY_THEME = {
  backgroundColor: '#F0FBFF',
  accentColor: '#007A7A',
  textColor: '#000000',
  buttonColor: '#E84F6B',
  buttonTextColor: '#ffffff',
  answerColor: '#cfcfcf',
  font: 'Lexend',
}

// A theme is "custom" (worth applying) only when it came from the database.
// The backend fallback theme has id 0 / no id.
export function isCustomSurveyTheme(theme) {
  return !!theme && Number(theme.id) > 0
}

const clean = value =>
  typeof value === 'string' && value.trim() ? value.trim() : null

// Build the inline style for the survey wrapper. Returns CSS custom properties
// (consumed by WbButton, the progress bar, etc.) plus the base background / text /
// font so the whole survey surface picks up the theme. Returns null when there is
// no custom theme, leaving the existing default styling untouched.
export function getSurveyThemeStyle(theme) {
  if (!isCustomSurveyTheme(theme)) return null

  const pick = (value, key) => clean(value) || DEFAULT_SURVEY_THEME[key]

  const background = pick(theme.backgroundColor, 'backgroundColor')
  const accent = pick(theme.accentColor, 'accentColor')
  const text = pick(theme.textColor, 'textColor')
  const button = pick(theme.buttonColor, 'buttonColor')
  const buttonText = pick(theme.buttonTextColor, 'buttonTextColor')
  const answer = pick(theme.answerColor, 'answerColor')
  const font = pick(theme.font, 'font')

  return {
    '--survey-bg': background,
    '--survey-accent': accent,
    '--survey-text': text,
    '--survey-button-color': button,
    '--survey-button-text': buttonText,
    '--survey-answer': answer,
    '--survey-font': font,
    backgroundColor: background,
    color: text,
    fontFamily: `${font}, Inter, sans-serif`,
    minHeight: '100vh',
  }
}
